package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.EntityFinancials
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.MonthlyFinancials
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.DirectorFinancialsRepository
import br.com.vvrefeicoes.portal.domain.repository.ManagerFinancialsRepository
import br.com.vvrefeicoes.portal.domain.repository.UnitFinancialsRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Agregações de diretoras/gestoras/unidades alimentadas pelo backend
 * (`GET /operational/directors|managers|units`).
 */
class RemoteEntityFinancialsRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
    private val endpoint: String,
) : ManagerFinancialsRepository,
    DirectorFinancialsRepository,
    UnitFinancialsRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): List<EntityFinancials> {
        val path = "$endpoint?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<List<EntityFinancialsDto>>().map { it.toDomain() }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
internal data class PanelApiErrorDto(
    val message: String? = null,
)

@Serializable
internal data class PanelMonthDto(
    val year: Int = 0,
    val month: Int = 1,
    val label: String = "",
)

@Serializable
private data class EntityFinancialsDto(
    val name: String = "",
    val totals: TotalsDto = TotalsDto(),
    val history: List<EntityMonthlyDto> = emptyList(),
)

@Serializable
private data class TotalsDto(
    val revenueCents: Long = 0,
    val expenseCents: Long = 0,
    val resultCents: Long = 0,
)

@Serializable
private data class EntityMonthlyDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val revenueCents: Long = 0,
    val expenseCents: Long = 0,
)

private fun EntityFinancialsDto.toDomain(): EntityFinancials {
    val history = history.map { MonthlyFinancials(MonthPeriod(it.period.year, it.period.month), it.revenueCents, it.expenseCents) }
    val totalsPeriod = history.lastOrNull()?.period ?: MonthPeriod(2000, 1)
    return EntityFinancials(
        name = name,
        monthly = history,
        periodTotals = listOf(MonthlyFinancials(totalsPeriod, totals.revenueCents, totals.expenseCents)),
    )
}
